import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { sdohAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend } from 'recharts';
import { FiLayers, FiPlus, FiX, FiHome, FiShoppingBag, FiTruck, FiBookOpen, FiBriefcase, FiWind, FiUploadCloud } from 'react-icons/fi';
import toast from 'react-hot-toast';
import CsvUploader from '../components/CsvUploader';

const COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899'];

const SDOH = () => {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const { canCreate, canEdit, isAdmin } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [form, setForm] = useState({
    region: 'Northeast', period: { year: 2024, quarter: 'Q4' },
    housing: { homelessRate: '', overcrowdingRate: '', affordabilityIndex: '' },
    foodSecurity: { foodInsecurityRate: '', snapParticipation: '' },
    transportation: { publicTransitAccess: '', averageCommute: '' },
    education: { highSchoolGradRate: '', collegeAttainment: '', healthLiteracy: '' },
    employment: { unemploymentRate: '', medianIncome: '', povertyRate: '', uninsuredRate: '' },
    overallRiskIndex: ''
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [recs, st] = await Promise.all([sdohAPI.getAll(), sdohAPI.getStats()]);
      setRecords(recs.data.records || recs.data || []);
      setStats(st.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const numForm = JSON.parse(JSON.stringify(form));
      const numFields = (obj) => { for (const k in obj) { if (typeof obj[k] === 'object') numFields(obj[k]); else if (obj[k] !== '') obj[k] = Number(obj[k]); } };
      numFields(numForm);
      await sdohAPI.create(numForm);
      toast.success('SDOH record added');
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  const radarData = records.map(r => ({
    region: r.region,
    Housing: (10 - (r.housing?.affordabilityIndex || 0) / 10).toFixed(1),
    Food: (r.foodSecurity?.foodInsecurityRate || 0),
    Transport: (100 - (r.transportation?.publicTransitAccess || 0)) / 10,
    Education: (100 - (r.education?.highSchoolGradRate || 0)) / 10,
    Employment: (r.employment?.unemploymentRate || 0),
    Poverty: (r.employment?.povertyRate || 0) / 3,
  }));

  const riskData = records.map(r => ({ name: r.region, risk: r.overallRiskIndex?.toFixed(1) || 0, poverty: r.employment?.povertyRate?.toFixed(1) || 0, uninsured: r.employment?.uninsuredRate?.toFixed(1) || 0 }));

  const categories = [
    { icon: FiHome, label: 'Housing', color: '#3b82f6', field: 'housing', metrics: [{ key: 'homelessRate', label: 'Homeless Rate %' }, { key: 'overcrowdingRate', label: 'Overcrowding %' }, { key: 'affordabilityIndex', label: 'Affordability Index' }] },
    { icon: FiShoppingBag, label: 'Food Security', color: '#10b981', field: 'foodSecurity', metrics: [{ key: 'foodInsecurityRate', label: 'Insecurity Rate %' }, { key: 'snapParticipation', label: 'SNAP Rate %' }] },
    { icon: FiTruck, label: 'Transportation', color: '#f59e0b', field: 'transportation', metrics: [{ key: 'publicTransitAccess', label: 'Transit Access %' }, { key: 'averageCommute', label: 'Avg Commute (min)' }] },
    { icon: FiBookOpen, label: 'Education', color: '#8b5cf6', field: 'education', metrics: [{ key: 'highSchoolGradRate', label: 'HS Grad Rate %' }, { key: 'collegeAttainment', label: 'College %' }, { key: 'healthLiteracy', label: 'Health Literacy %' }] },
    { icon: FiBriefcase, label: 'Employment', color: '#ef4444', field: 'employment', metrics: [{ key: 'unemploymentRate', label: 'Unemployment %' }, { key: 'medianIncome', label: 'Median Income $' }, { key: 'povertyRate', label: 'Poverty Rate %' }, { key: 'uninsuredRate', label: 'Uninsured %' }] },
  ];

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>Social Determinants of Health</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Housing, food security, transportation, education, and employment tracking</p>
        </div>
        <div className="page-header-actions" style={{ display: 'flex', gap: 10 }}>
          {canCreate && <button onClick={() => setShowCsvUpload(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#f0f9ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            <FiUploadCloud size={18} /> CSV Upload
          </button>}
          {canCreate && <button onClick={() => setShowModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            <FiPlus size={18} /> Add Record
          </button>}
        </div>
      </div>

      {/* Risk Overview */}
      <div className="chart-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>SDOH Risk Index by Region</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={riskData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="risk" fill="#ef4444" radius={[4, 4, 0, 0]} name="Risk Index" />
              <Bar dataKey="poverty" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Poverty %" />
              <Bar dataKey="uninsured" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Uninsured %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>SDOH Risk Radar</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={[radarData[0] || {}]}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis tick={{ fontSize: 10 }} />
              {radarData.length > 0 && ['Housing', 'Food', 'Transport', 'Education', 'Employment', 'Poverty'].map((key, i) => (
                <Radar key={key} name={key} dataKey={key} stroke={COLORS[i]} fill={COLORS[i]} fillOpacity={0.1} />
              ))}
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Cards */}
      <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
        {categories.map((cat) => {
          const Icon = cat.icon;
          return (
            <div key={cat.label} style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: `${cat.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={20} color={cat.color} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{cat.label}</h3>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {records.slice(0, 3).map((r) => (
                  <div key={r._id} style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{r.region}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {cat.metrics.map(m => (
                        <span key={m.key} style={{ fontSize: 12 }}>
                          <span style={{ color: '#64748b' }}>{m.label}: </span>
                          <span style={{ fontWeight: 600 }}>{r[cat.field]?.[m.key]?.toFixed?.(1) ?? r[cat.field]?.[m.key] ?? '—'}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Resource Directory */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Resource Directory</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          {records.flatMap(r => (r.resources || []).map((res, i) => (
            <div key={`${r._id}-${i}`} style={{ padding: '14px 18px', background: '#f0fdf4', borderRadius: 10, borderLeft: '4px solid #10b981' }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#166534' }}>{res.name}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{res.type} — {res.address}</div>
              {res.phone && <div style={{ fontSize: 13, color: '#3b82f6', marginTop: 2 }}>{res.phone}</div>}
            </div>
          )))}
          {records.every(r => !r.resources?.length) && <div style={{ color: '#94a3b8', padding: 20 }}>No resources listed</div>}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700 }}>Add SDOH Record</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Region</label>
                  <select value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} style={inputStyle}>
                    {['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'Pacific Northwest'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Overall Risk Index (0-10)</label>
                  <input type="number" step="0.1" value={form.overallRiskIndex} onChange={e => setForm({ ...form, overallRiskIndex: e.target.value })} style={inputStyle} />
                </div>
              </div>
              {categories.map(cat => (
                <div key={cat.field} style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: cat.color, marginBottom: 8 }}>{cat.label}</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(cat.metrics.length, 3)}, 1fr)`, gap: 10 }}>
                    {cat.metrics.map(m => (
                      <div key={m.key}>
                        <label style={{ ...labelStyle, fontSize: 12 }}>{m.label}</label>
                        <input type="number" step="0.1" value={form[cat.field]?.[m.key] || ''} onChange={e => setForm({ ...form, [cat.field]: { ...form[cat.field], [m.key]: e.target.value } })} style={inputStyle} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <button type="submit" style={{ width: '100%', padding: 12, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Add Record</button>
            </form>
          </div>
        </div>
      )}

      {showCsvUpload && (
        <CsvUploader
          title="SDOH Records"
          templateColumns={['region','year','quarter','risk_index','homeless_rate','overcrowding_rate','affordability_index','food_insecurity_rate','snap_rate','transit_access','avg_commute','hs_grad_rate','college_rate','health_literacy','unemployment_rate','median_income','poverty_rate','uninsured_rate']}
          templateSampleRows={[
            ['Northeast','2024','Q4','4.2','1.2','5.5','45','12','18','65','32','88','35','62','5.2','52000','11','8'],
            ['Southeast','2024','Q4','6.1','1.8','8.2','38','18','25','42','28','82','28','55','7.1','42000','16','12'],
            ['Midwest','2024','Q4','5.0','0.9','4.1','50','15','20','55','25','90','32','68','4.8','48000','13','9']
          ]}
          onUpload={(formData) => sdohAPI.bulkImport(formData)}
          onClose={() => { setShowCsvUpload(false); fetchData(); }}
        />
      )}
    </div>
  );
};

const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };

export default SDOH;
